import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSession } from "@clerk/react";
import { Button, Card, Chip } from "@heroui/react";
import { Mic, Pause, Play, UserPlus, X } from "lucide-react";
import { useWakeLock } from "react-screen-wake-lock";
import { SETTINGS } from "../../utils/settings";
import { getCurrentDateTime, isMeetingEnded } from "@/utils/dates";
import {
  getByShortCode as getByShortCodeApi,
  setMeetsStartTime as setMeetsStartTimeApi,
  updateMeet as updateMeetApi,
} from "@/api/meets";
import { metrics, startSession, recordSectionStart, recordSectionEnd, summarize } from "@/utils/metrics";
import AddMembersDrawer from "../drawers/add-members-drawer";
import { toConnectionShape } from "@/utils/meets";
import { cn } from "@/utils/cn";

const API_URL = `${SETTINGS["API_ENDPOINT"]}/audio/api`;
const SECTION_MS = 10_000;

const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const extFromMime = (mime) => {
  if (!mime) return ".webm";
  const base = mime.split(";")[0].toLowerCase();
  if (base.includes("wav")) return ".wav";
  if (base.includes("mp4")) return ".m4a";
  if (base.includes("ogg")) return ".ogg";
  if (base.includes("webm")) return ".webm";
  return ".webm";
};

function floatTo16BitPCM(float32Array) {
  const output = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    let s = Math.max(-1, Math.min(1, float32Array[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

function encodeWAV(float32Data, sampleRate) {
  const channelCount = 1;
  const pcm16 = floatTo16BitPCM(float32Data);
  const bytesPerSample = 2;
  const blockAlign = channelCount * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = pcm16.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  writeString(view, 22, "\x01\x00");
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);
  let offset = 44;
  for (let i = 0; i < pcm16.length; i++, offset += 2) {
    view.setInt16(offset, pcm16[i], true);
  }
  return new Blob([view], { type: "audio/wav" });
}

function resampleLinear(float32, fromRate, toRate) {
  if (fromRate === toRate) return float32;
  const ratio = toRate / fromRate;
  const newLen = Math.max(1, Math.round(float32.length * ratio));
  const out = new Float32Array(newLen);
  const step = (float32.length - 1) / (newLen - 1);
  for (let i = 0; i < newLen; i++) {
    const pos = i * step;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, float32.length - 1);
    const t = pos - i0;
    out[i] = float32[i0] * (1 - t) + float32[i1] * t;
  }
  return out;
}

function getMeetingStartTimestamp(record) {
  const real = record?.start_time?.real;
  const dateStr = record?.meeting_date;
  if (!real || !dateStr) return null;
  const ts = new Date(`${dateStr}T${real}:00`).getTime();
  return Number.isNaN(ts) ? null : ts;
}

const SkeletonBar = ({ className = "h-4 w-24" }) => (
  <span className={cn("inline-block rounded-full bg-default/40 animate-pulse align-middle", className)} />
);

// Static bar heights — a decorative visualiser, not tied to real amplitude.
// Inline styles rather than dynamic Tailwind classes so JIT can't drop them.
const WAVE_BARS = [
  28, 46, 62, 40, 74, 55, 88, 66, 42, 70, 94, 58, 36, 68, 82, 50, 30, 60, 76, 44, 90, 54, 34, 72, 86, 48, 64, 38, 80,
  56, 26, 68, 92, 52, 40, 74, 60, 32, 84, 46,
];

const WaveVisualiser = ({ isActive }) => (
  <div className="flex h-24 items-center justify-center gap-1 overflow-hidden">
    {WAVE_BARS.map((height, i) => (
      <span
        key={i}
        className={cn(
          "w-1.5 rounded-full transition-all duration-500",
          isActive ? "bg-accent motion-safe:animate-pulse" : "bg-default",
        )}
        style={{
          height: `${isActive ? height : Math.max(8, height * 0.25)}%`,
          animationDelay: `${(i % 10) * 90}ms`,
        }}
      />
    ))}
  </div>
);

const DesktopActiveMeetsComp = ({ _short_code }) => {
  const { session } = useSession();
  const navigate = useNavigate();

  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [status, setStatus] = useState("Idle");
  const [recordedTime, setRecordedTime] = useState(0);
  const [stitchingAudio, setStitchingAudio] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [meetingStarted, setMeetingStarted] = useState(false);
  const [showAddMemeber, setShowAddMember] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const pendingUploadsRef = useRef(new Set());
  const failedUploadsRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const micStreamRef = useRef(null);
  const sectionIndexRef = useRef(0);
  const timerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const procRef = useRef(null);
  const wavSampleRateRef = useRef(0);
  const wavBufferRef = useRef(new Float32Array(0));
  const wavSamplesPerSectionRef = useRef(0);
  const safariPausedRef = useRef(false);
  const hasSetStartTimeRef = useRef(false);
  const autoStartedRef = useRef(false);

  const meetingStartTsRef = useRef(null);

  const { isSupported, request, release } = useWakeLock();

  useEffect(() => {
    getMeetingRec();
    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Your browser does not support audio recording");
    }
    return () => {
      try {
        clearInterval(timerRef.current);
        stopSafariWavIfRunning();
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
        micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
        micStreamRef.current = null;
      } catch {}
      releaseWakeLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && recording) {
        setTimeout(acquireWakeLock, 50);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, isSupported]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (recording) {
        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [recording]);

  useEffect(() => {
    if (meeting && !meeting.completed && meeting.created_by?.user_id === session?.user?.id && !isMeetingEnded(meeting) && !autoStartedRef.current) {
      autoStartedRef.current = true;
      startRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting]);

  async function getMeetingRec() {
    try {
      const token = await session.getToken();
      const _resp = await getByShortCodeApi(_short_code, token);
      if (_resp.data.Result === "OK") {
        const record = _resp.data.Record;
        const real_end_time = record?.end_time?.real || record?.end_time?.set;
        if (record.completed || record.created_by?.user_id !== session?.user?.id || isMeetingEnded(record)) return navigate(`/recordings?selected=${encodeURIComponent(record._id)}`);
        setMeeting(record);

        const isStarted = record?.start_time?.real && record.start_time.real !== "";
        setMeetingStarted(Boolean(isStarted));
        hasSetStartTimeRef.current = Boolean(isStarted);

        if (isStarted) {
          const startTs = getMeetingStartTimestamp(record);
          if (startTs) {
            meetingStartTsRef.current = startTs;
            setRecordedTime(Math.max(0, Math.floor((Date.now() - startTs) / 1000)));
          }
        }
      } else {
        setMeeting(null);
      }
    } catch (e) {
      console.error(e);
      setMeeting(null);
    }
  }

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const pickMimeType = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/ogg", "audio/mp4"];
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported?.(type)) return type;
    }
    return "";
  };

  const uploadSection = (blob, sectionIndex) => {
    const upload = sendSection(blob, sectionIndex);
    pendingUploadsRef.current.add(upload);
    upload.finally(() => pendingUploadsRef.current.delete(upload));
    return upload;
  };

  const sendSection = async (blob, sectionIndex) => {
    recordSectionStart(sectionIndex, blob.size);
    const ext = extFromMime(blob.type);
    const filename = `section-${sectionIndex.toString().padStart(6, "0")}${ext}`;
    const typedBlob = new Blob([blob], { type: blob.type || "audio/webm" });
    const current_date_time = getCurrentDateTime();
    const formData = new FormData();
    formData.append("file", typedBlob, filename);
    formData.append("meeting_id", meeting?._id || "");
    formData.append("section_index", sectionIndex);
    formData.append("blob_mime", typedBlob.type || "");
    formData.append("date_time", current_date_time);
    try {
      const token = await session.getToken();
      const res = await axios.post(`${API_URL}/upload-section`, formData, {
        headers: { "Content-Type": "multipart/form-data", TOKEN: token },
      });
      recordSectionEnd(sectionIndex, true, res.status);
    } catch (err) {
      recordSectionEnd(sectionIndex, false, err?.response?.status || "ERR_UPLOAD");
      failedUploadsRef.current = true;
      console.error("Upload section failed:", err);
    }
  };

  const updateMeet = async (record) => {
    setIsUpdating(true);
    const token = await session.getToken();

    updateMeetApi(record, token)
      .then((_resp) => {
        if (_resp.data.Result === "SUCCESS") {
          getMeetingRec();
        } else {
          throw new Error("Failed to update meet");
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  async function onAddMemberDrawerResp(_cmd, _value) {
    if (_cmd === "close") {
      setShowAddMember(false);
    } else if (_cmd === "confirm") {
      const payload = {
        ...meeting,
        members: _value.map((m) => m.id),
      };
      await updateMeet(payload);
      setShowAddMember(false);
    }
  }

  const acquireWakeLock = async () => {
    if (!isSupported) return;
    try {
      await request();
    } catch (e) {
      console.warn("WakeLock request failed:", e);
    }
  };

  const releaseWakeLock = async () => {
    if (!isSupported) return;
    try {
      await release();
    } catch (e) {
      console.warn("WakeLock release failed:", e);
    }
  };

  async function getFreshMicStream() {
    return await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
    });
  }

  async function ensureMicPermission() {
    return await getFreshMicStream();
  }

  const startMediaRecorder = async (providedStream) => {
    const stream = providedStream || (await ensureMicPermission());
    micStreamRef.current = stream;
    const mimeType = pickMimeType();
    const opts = mimeType ? { mimeType, audioBitsPerSecond: 24000 } : { audioBitsPerSecond: 24000 };
    const recorder = new MediaRecorder(stream, opts);
    if (!recording && !paused) {
      sectionIndexRef.current = 0;
    }
    recorder.ondataavailable = async (event) => {
      if (event.data?.size > 0) {
        const idx = sectionIndexRef.current++;
        await uploadSection(event.data, idx);
      }
    };
    recorder.onstop = async () => {
      clearInterval(timerRef.current);
      await releaseWakeLock();
      await finalizeSession(micStreamRef.current);
    };
    recorder.start(SECTION_MS);
    mediaRecorderRef.current = recorder;
    startTicking();
    setStatus("Recording");
    setRecording(true);
    setPaused(false);
  };

  const startSafariWav = async (providedStream) => {
    const stream = providedStream || (await ensureMicPermission());
    micStreamRef.current = stream;
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    wavSampleRateRef.current = audioCtx.sampleRate;
    if (!recording && !paused) {
      wavBufferRef.current = new Float32Array(0);
      sectionIndexRef.current = 0;
    }
    wavSamplesPerSectionRef.current = Math.floor((wavSampleRateRef.current * SECTION_MS) / 1000);
    safariPausedRef.current = false;
    processor.onaudioprocess = async (e) => {
      if (safariPausedRef.current) return;
      const input = e.inputBuffer.getChannelData(0);
      const prev = wavBufferRef.current;
      const merged = new Float32Array(prev.length + input.length);
      merged.set(prev, 0);
      merged.set(input, prev.length);
      wavBufferRef.current = merged;
      while (wavBufferRef.current.length >= wavSamplesPerSectionRef.current) {
        const section = wavBufferRef.current.slice(0, wavSamplesPerSectionRef.current);
        wavBufferRef.current = wavBufferRef.current.slice(wavSamplesPerSectionRef.current);
        const TARGET_SR = 16000;
        const section16k = resampleLinear(section, wavSampleRateRef.current, TARGET_SR);
        const wavBlob = encodeWAV(section16k, TARGET_SR);
        const idx = sectionIndexRef.current++;
        await uploadSection(new Blob([wavBlob], { type: "audio/wav" }), idx);
      }
    };
    source.connect(processor);
    processor.connect(audioCtx.destination);
    audioCtxRef.current = audioCtx;
    sourceRef.current = source;
    procRef.current = processor;
    startTicking();
    setStatus("Recording");
    setRecording(true);
    setPaused(false);
  };

  const stopSafariWavIfRunning = () => {
    try {
      if (procRef.current) procRef.current.disconnect();
      if (sourceRef.current) sourceRef.current.disconnect();
      if (audioCtxRef.current) audioCtxRef.current.close();
    } catch {}
    procRef.current = null;
    sourceRef.current = null;
    audioCtxRef.current = null;
  };

  const startTicking = () => {
    clearInterval(timerRef.current);
    const tick = () => {
      if (meetingStartTsRef.current) {
        setRecordedTime(Math.max(0, Math.floor((Date.now() - meetingStartTsRef.current) / 1000)));
      } else {
        setRecordedTime((prev) => prev + 1);
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  };

  const stopTicking = () => clearInterval(timerRef.current);

  const finalizeSession = async (stream) => {
    setStitchingAudio(true);
    try {
      await Promise.all([...pendingUploadsRef.current]);
      if (failedUploadsRef.current) {
        throw new Error("Some audio sections failed to upload. Recording could not be completed.");
      }
      const token = await session.getToken();
      const res = await axios.post(
        `${API_URL}/complete-recording`,
        { short_code: _short_code, session_id: metrics.sessionId },
        { headers: { "Content-Type": "application/json", TOKEN: token } },
      );
      console.log("Recording complete:", res.data);
      navigate(`/recordings?selected=${encodeURIComponent(meeting._id)}`);
    } catch (e) {
      console.error("Finalize failed:", e);
      window.alert(e.message || "Recording could not be completed. Please try again.");
    }
    setStitchingAudio(false);
    console.log("Recording metrics summary:", summarize());
    setRecording(false);
    setPaused(false);
    setStatus("Idle");
    try {
      stream?.getTracks?.().forEach((t) => t.stop());
      micStreamRef.current = null;
    } catch {}
  };

  const startRecording = async () => {
    try {
      const freshStream = await ensureMicPermission();
      await acquireWakeLock();
      startSession();
      if (!meetingStarted && !hasSetStartTimeRef.current) await setMeetingStartTime();
      if (isSafari()) {
        await startSafariWav(freshStream);
      } else {
        await startMediaRecorder(freshStream);
      }
    } catch (err) {
      console.error("Recording error:", err);
      alert("Recording failed: " + err.message);
      await releaseWakeLock();
      try {
        micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
        micStreamRef.current = null;
      } catch {}
    }
  };

  async function setMeetingStartTime() {
    try {
      const token = await session.getToken();

      const requestedAt = Date.now();
      const _resp = await setMeetsStartTimeApi(meeting["_id"], token);
      if (_resp?.data?.Result === "SUCCESS") {
        setMeetingStarted(true);
        hasSetStartTimeRef.current = true;
        if (!meetingStartTsRef.current) {
          meetingStartTsRef.current = requestedAt;
        }
      }
    } catch (err) {
      console.error("setMeetingStartTime failed:", err);
    }
  }

  const pauseRecording = async () => {
    try {
      if (!recording || paused) return;
      if (isSafari()) {
        safariPausedRef.current = true;
      } else {
        mediaRecorderRef.current?.pause?.();
      }
      setPaused(true);
      stopTicking();
      setStatus("Paused");
    } catch (e) {
      console.error("Pause failed:", e);
    }
  };

  const resumeRecording = async () => {
    try {
      if (!recording || !paused) return;
      await acquireWakeLock();
      if (isSafari()) {
        stopSafariWavIfRunning();
        micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
        micStreamRef.current = null;
        await startSafariWav(null);
      } else {
        if (mediaRecorderRef.current?.state === "paused") {
          mediaRecorderRef.current.resume();
        } else {
          mediaRecorderRef.current = null;
          micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
          micStreamRef.current = null;
          await startMediaRecorder(null);
        }
      }
      setPaused(false);
      startTicking();
      setStatus("Recording");
    } catch (e) {
      console.error("Resume failed, attempting full restart:", e);
      await startRecording();
    }
  };

  const finishRecording = async () => {
    try {
      if (!recording) return;
      if (isSafari()) {
        stopTicking();
        stopSafariWavIfRunning();
        const remaining = wavBufferRef.current;
        if (remaining && remaining.length > 2000) {
          const TARGET_SR = 16000;
          const rem16k = resampleLinear(remaining, wavSampleRateRef.current, TARGET_SR);
          const wavBlob = encodeWAV(rem16k, TARGET_SR);
          const idx = sectionIndexRef.current++;
          await uploadSection(new Blob([wavBlob], { type: "audio/wav" }), idx);
        }
        await releaseWakeLock();
        await finalizeSession(micStreamRef.current || new MediaStream());
      } else {
        if (mediaRecorderRef.current?.state === "paused") {
          try {
            mediaRecorderRef.current.resume();
          } catch {}
        }
        mediaRecorderRef.current?.stop();
        stopTicking();
        await releaseWakeLock();
      }
    } catch (e) {
      console.error("Finish failed:", e);
    }
  };

  useEffect(() => {
    if (recording && !meetingStarted && meeting && !hasSetStartTimeRef.current) {
      setMeetingStartTime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, meetingStarted, meeting]);

  const safeDescription = useMemo(() => {
    const text = meeting?.description || "";
    return text.length > 200 ? text.substring(0, 200) + "..." : text;
  }, [meeting]);

  const isLoading = meeting === null;
  const participantCount = meeting?.members?.length ?? 0;
  const isCapturing = recording && !paused;

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-3xl flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              {isLoading ? (
                <SkeletonBar className="h-7 w-56" />
              ) : (
                <span className="text-2xl font-extrabold capitalize">{meeting.title}</span>
              )}

              {isCapturing && (
                <Chip variant="soft" color="danger" size="sm">
                  <Chip.Label className="flex items-center gap-1.5">
                    <span className="relative flex size-2">
                      <span className="absolute inline-flex size-full rounded-full bg-danger opacity-60 motion-safe:animate-ping" />
                      <span className="relative inline-flex size-2 rounded-full bg-danger" />
                    </span>
                    Recording
                  </Chip.Label>
                </Chip>
              )}

              {paused && (
                <Chip variant="soft" color="warning" size="sm">
                  <Chip.Label>Paused</Chip.Label>
                </Chip>
              )}
            </div>

            {isLoading ? (
              <SkeletonBar className="h-4 w-72" />
            ) : (
              safeDescription && <span className="text-sm text-muted">{safeDescription}</span>
            )}
          </div>

          <Button isIconOnly variant="ghost" onPress={() => navigate("/")} aria-label="Leave recording">
            <X size={18} />
          </Button>
        </div>

        {/* Recorder */}
        <Card>
          <Card.Content className="items-center gap-6 py-10">
            <div className="flex flex-col items-center gap-1">
              {isLoading ? (
                <SkeletonBar className="h-14 w-44" />
              ) : (
                <span className="text-6xl font-bold tabular-nums tracking-tight">{formatTime(recordedTime)}</span>
              )}
              <span className="text-sm text-muted">Status: {status}</span>
            </div>

            <div className="w-full max-w-xl">
              <WaveVisualiser isActive={isCapturing} />
            </div>

            <div className="flex items-center gap-3">
              {!paused ? (
                <Button
                  variant="outline"
                  size="lg"
                  className="min-w-[160px] rounded-full"
                  onPress={pauseRecording}
                  isDisabled={isLoading || !recording || stitchingAudio}
                >
                  <Pause size={18} />
                  Pause
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="lg"
                  className="min-w-[160px] rounded-full"
                  onPress={resumeRecording}
                  isDisabled={isLoading || stitchingAudio}
                >
                  <Play size={18} />
                  Continue
                </Button>
              )}

              <Button
                variant="primary"
                size="lg"
                className="min-w-[160px] rounded-full"
                onPress={finishRecording}
                isPending={stitchingAudio}
                isDisabled={isLoading || !recording || stitchingAudio}
              >
                {stitchingAudio ? "Finishing..." : "Finish"}
              </Button>
            </div>
          </Card.Content>
        </Card>

        {/* Participants */}
        <Card>
          <Card.Content className="flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-accent-soft text-accent">
                <Mic size={18} />
              </div>

              <div className="flex flex-col">
                {isUpdating || isLoading ? (
                  <SkeletonBar className="h-4 w-40" />
                ) : (
                  <span className="font-semibold text-sm">{participantCount} participants</span>
                )}
                <span className="text-xs text-muted">Everyone in this Loopp</span>
              </div>
            </div>

            <Button variant="outline" onPress={() => setShowAddMember(true)} isDisabled={isUpdating || isLoading}>
              <UserPlus size={16} />
              Add people
            </Button>
          </Card.Content>
        </Card>
      </div>

      <AddMembersDrawer
        show={showAddMemeber}
        onResp={onAddMemberDrawerResp}
        initialParticipants={toConnectionShape(meeting?.members || [])}
        isConfirming={isUpdating}
      />
    </div>
  );
};

export default DesktopActiveMeetsComp;
